import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Loader2, ArrowLeft, Trash2, Image as ImageIcon } from "lucide-react";
import { ShareButtons } from "@/components/ShareButtons";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Generation {
  id: string;
  hairstyle_name: string;
  original_photo_url: string;
  result_photo_url: string;
  created_at: string;
}

const LazyImage = ({ src, alt, label }: { src: string; alt: string; label: string }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            setHasError(false);
            setIsLoading(true);
            if (imgRef.current && !imgRef.current.src) {
              imgRef.current.src = src;
            }
          } else {
            // Unload image when out of view to free memory
            if (imgRef.current && imgRef.current.src) {
              imgRef.current.src = '';
              setIsVisible(false);
              setIsLoading(true);
            }
          }
        });
      },
      { rootMargin: "100px" }
    );

    const currentRef = imgRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      observer.disconnect();
    };
  }, [src]);

  return (
    <div className="aspect-square relative bg-muted">
      <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded z-10">
        {label}
      </div>
      {isLoading && !hasError && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}
      {hasError ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground">
          <ImageIcon className="h-8 w-8 mb-2" />
          <span className="text-xs">Failed to load</span>
        </div>
      ) : (
        <img
          ref={imgRef}
          alt={alt}
          className="w-full h-full object-cover"
          loading="lazy"
          onLoad={() => setIsLoading(false)}
          onError={() => {
            setIsLoading(false);
            setHasError(true);
          }}
        />
      )}
    </div>
  );
};

const History = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [generations, setGenerations] = useState<Generation[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const PAGE_SIZE = 10;

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      fetchGenerations(true);
    }
  }, [user]);

  const fetchGenerations = async (reset: boolean = false) => {
    if (reset) {
      setLoading(true);
      setGenerations([]);
    } else {
      setLoadingMore(true);
    }
    setError(null);
    
    try {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      const from = reset ? 0 : generations.length;
      const to = from + PAGE_SIZE - 1;

      const { data, error, count } = await supabase
        .from('generations')
        .select('*', { count: 'exact' })
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) throw error;
      
      if (reset) {
        setGenerations(data || []);
      } else {
        setGenerations(prev => [...prev, ...(data || [])]);
      }
      
      setHasMore((data?.length || 0) === PAGE_SIZE);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      const { error } = await supabase
        .from('generations')
        .delete()
        .eq('id', deleteId);

      if (error) throw error;

      fetchGenerations(true);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setDeleteId(null);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary p-4">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate("/")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            My Generations
          </h1>
          <div className="w-24" />
        </div>

        {error ? (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-destructive font-semibold mb-2">Connection Error</p>
              <p className="text-muted-foreground mb-4">
                {error}
              </p>
              <Button onClick={() => fetchGenerations(true)}>
                Try Again
              </Button>
            </CardContent>
          </Card>
        ) : generations.length === 0 && !loading ? (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-muted-foreground">
                No generations yet. Try on a hairstyle to get started!
              </p>
              <Button onClick={() => navigate("/")} className="mt-4">
                Try Hairstyles
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {generations.map((gen) => (
              <Card key={gen.id} className="overflow-hidden">
                <div className="grid grid-cols-2 gap-1">
                  <LazyImage 
                    src={gen.original_photo_url}
                    alt="Original"
                    label="Original"
                  />
                  <LazyImage 
                    src={gen.result_photo_url}
                    alt="Result"
                    label="Result"
                  />
                </div>
                <CardContent className="p-4 space-y-2">
                  <h3 className="font-semibold">{gen.hairstyle_name}</h3>
                  <p className="text-xs text-muted-foreground">
                    {new Date(gen.created_at).toLocaleDateString()}
                  </p>
                  <ShareButtons 
                    imageUrl={gen.result_photo_url}
                    hairstyleName={gen.hairstyle_name}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => setDeleteId(gen.id)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        {hasMore && !error && (
          <div className="flex justify-center mt-6">
            <Button 
              onClick={() => fetchGenerations(false)} 
              disabled={loadingMore}
              variant="outline"
            >
              {loadingMore ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading...
                </>
              ) : (
                'Load More'
              )}
            </Button>
          </div>
        )}
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete generation?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete this generation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default History;
